import { useCallback } from "react";
import { useStore } from "react-redux";
import { Feature, MultiPolygon, Polygon } from "geojson";

import { useUpdateVersionMutation } from "fond/api";
import { togglePolygonLayer } from "fond/project/polygon/redux";
import { POLYGON_SELECT_UPLOAD_ID, polygonSelect, polygonSelectUpload, setActiveLayerGroupId } from "fond/project/redux";
import { dismissBaseDataTask, failBaseDataTask, startBaseDataTask, succeedBaseDataTask } from "fond/redux/baseDataTasks";
import { Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { IMPORT_SAVE_ERROR } from "../copy";
import { BASE_DATA_LAYERS } from "../layers";
import type { BaseDataTaskSource } from "../taskPhases";

import {
  BOUNDARY_POLYGON_SELECT_LAYER_IDS,
  BoundaryImportKey,
  getImportCompanion,
  getImportSources,
  getSelectedCounts,
  toMultiPolygonFeature,
} from "./boundary";
import { formatImportResult, ImportCounts } from "./importTask";

/** Return a callback that begins polygon select for the boundary, with addresses, parcels and streets selected for import. */
export const useBeginBoundarySelect = (): (() => void) => {
  const dispatch = useAppDispatch();

  return useCallback(() => {
    // Polygon select imports addresses, parcels and streets when no layer group is active.
    dispatch(setActiveLayerGroupId(null));
    dispatch(polygonSelect({ layerIds: BOUNDARY_POLYGON_SELECT_LAYER_IDS }));
  }, [dispatch]);
};

/** Return a callback that selects exactly the given layers in polygon select. */
const useSelectPolygonLayers = (): ((layerIds: string[]) => void) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();

  return useCallback(
    (layerIds: string[]) => {
      const { selectedLayers } = store.getState().project.polygon;
      Object.entries(selectedLayers)
        .filter(([layerId, selected]) => !!selected !== layerIds.includes(layerId))
        .forEach(([layerId]) => dispatch(togglePolygonLayer(layerId)));
    },
    [dispatch, store]
  );
};

/** Return a callback that begins polygon select for importing the FOND data of a base data layer, and of its import companion if any. */
export const useBeginImportAreaSelect = (): ((key: BoundaryImportKey) => void) => {
  const dispatch = useAppDispatch();
  const selectPolygonLayers = useSelectPolygonLayers();

  return useCallback(
    (key: BoundaryImportKey) => {
      const companion = getImportCompanion(key);
      const layerIds = getImportSources(companion ? [key, companion] : [key]);
      // Polygon select reads the active layer group to decide which layers to import.
      dispatch(setActiveLayerGroupId(BASE_DATA_LAYERS[key].layerGroupId));
      dispatch(polygonSelect({ layerIds }));
      // Polygon select also selects parcels for addresses, so select exactly these layers.
      selectPolygonLayers(layerIds);
    },
    [dispatch, selectPolygonLayers]
  );
};

/** An import of FOND data inside an area selected in polygon select. */
export type AreaImportSource = Extract<BaseDataTaskSource, "drawnAreaImport" | "uploadedAreaImport">;

/**
 * Return a callback that imports the FOND data of base data layers inside the area selected in polygon select, as a task of the row whose
 * flyout started it.
 */
export const useUploadImportArea = (): ((owner: BoundaryImportKey, source: AreaImportSource, keys: BoundaryImportKey[]) => Promise<void>) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const selectPolygonLayers = useSelectPolygonLayers();

  return useCallback(
    async (owner: BoundaryImportKey, source: AreaImportSource, keys: BoundaryImportKey[]) => {
      const { selectedData } = store.getState().project.polygon;
      const counts: ImportCounts = Object.fromEntries(keys.map((key) => [key, getSelectedCounts(selectedData, key)]));
      selectPolygonLayers(getImportSources(keys));
      // Polygon select leaves addresses out of an import into the parcels layer group.
      if (keys.includes("addresses")) dispatch(setActiveLayerGroupId(BASE_DATA_LAYERS.addresses.layerGroupId));
      dispatch(startBaseDataTask({ key: owner, source, keys, stage: "uploading", uploadId: POLYGON_SELECT_UPLOAD_ID }));
      try {
        await dispatch(polygonSelectUpload(false, { saveArea: false }));
      } catch {
        dispatch(failBaseDataTask({ key: owner, error: IMPORT_SAVE_ERROR }));
        return;
      }
      dispatch(succeedBaseDataTask({ key: owner, result: [{ message: formatImportResult(counts) }] }));
    },
    [dispatch, selectPolygonLayers, store]
  );
};

/** A task that saves the boundary: a polygon uploaded from a file, or drawn on the map. */
export type BoundarySaveSource = Extract<BaseDataTaskSource, "boundaryUpload" | "boundarySave">;

/**
 * Return a callback that sets the version's boundary (`version.UploadedArea`) without importing any data, as a task of the Boundary row.
 *
 * The callback rejects when the boundary couldn't be saved, leaving the task failed.
 */
export const useSaveBoundary = (): {
  saveBoundary(area: Feature<Polygon | MultiPolygon>, source: BoundarySaveSource): Promise<void>;
  isSaving: boolean;
} => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const [updateVersion, { isLoading }] = useUpdateVersionMutation();

  const saveBoundary = useCallback(
    async (area: Feature<Polygon | MultiPolygon>, source: BoundarySaveSource) => {
      dispatch(startBaseDataTask({ key: "boundary", source, keys: ["boundary"], stage: "savingBoundary" }));
      try {
        await updateVersion({ ID: versionId, UploadedArea: toMultiPolygonFeature(area) }).unwrap();
      } catch (error) {
        dispatch(failBaseDataTask({ key: "boundary" }));
        throw error;
      }
      dispatch(dismissBaseDataTask("boundary"));
    },
    [dispatch, updateVersion, versionId]
  );

  return { saveBoundary, isSaving: isLoading };
};

/** Return a callback that removes the version's boundary (`version.UploadedArea`). */
export const useClearBoundary = (): (() => Promise<void>) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const [updateVersion] = useUpdateVersionMutation();

  return useCallback(async () => {
    await updateVersion({ ID: versionId, UploadedArea: null }).unwrap();
  }, [updateVersion, versionId]);
};
